import type {
  Panel,
  Panels,
  PanelName,
  PanelPath,
  Parameters,
  ResolvedPanel,
} from './types';

export const first = (path?: PanelPath | null) =>
  path && path.length ? path[0] : null;
export const last = (path?: PanelPath | null) =>
  path && path.length ? path[path.length - 1] : null;

export const makeNamespacedPanelName = (prefix: string, panelName: string) =>
  prefix + '/' + panelName;

// example:
// getNthFromEnd(['siteSettings', 'ecommerce', 'taxes', 'taxesMain'], 3) -> 'ecommerce'
export const getNthFromEnd = (
  array: PanelPath,
  index: number
): PanelName | null => array.slice(-index, 1 - index)[0] || null;

type PanelsLevel = {
  byName: Partial<Record<PanelName, Panel<any>>>;
  metadata: {
    parameter?: string;
  };
};
// follows the target path; returns contextual information
// about whether the target matches the specified current
// path, and whether the panel config exists
export function checkPath(
  panels: Panels,
  from: {
    path?: PanelPath | undefined;
    parameters?: {
      [key: string]: any;
    };
  } = {},
  to: {
    path?: PanelPath | undefined;
    parameters?: {
      [key: string]: any;
    };
  } = {}
) {
  // type casting here because we're destructuring from an array
  // and also providing fallbacks, so all cases are covered
  const {path: currentPath = [], parameters: currentParams = {}} = from;
  const {path: targetPath = [], parameters: targetParams = {}} = to;
  const bothPathsExist = Boolean(targetPath.length && currentPath.length);
  const segmentsToCheck = Math.max(currentPath.length, targetPath.length);

  if (!segmentsToCheck) {
    // if segmentsToCheck is 0, both paths are empty
    return {
      isAnyMatch: true,
      isChild: false,
      isExactMatch: true,
      isParent: false,
      panelExists: false,
    };
  }

  // @ts-expect-error - TS2352 - Conversion of type 'Panels' to type 'PanelsLevel' may be a mistake because neither type sufficiently overlaps with the other. If this was intentional, convert the expression to 'unknown' first.
  let panel = panels as PanelsLevel;
  let isParent = bothPathsExist && targetPath.length > currentPath.length;
  let isChild = bothPathsExist && targetPath.length < currentPath.length;
  let isExactMatch = targetPath.length === currentPath.length;

  for (let i = 0; i < segmentsToCheck; i++) {
    const panelName = targetPath[i];
    // @ts-expect-error - TS2322 - Type 'PanelsLevel | null' is not assignable to type 'PanelsLevel'.
    panel =
      panel && panel.byName && panelName
        ? // @ts-expect-error - TS2352 - Conversion of type 'Panel<any> | undefined' to type 'PanelsLevel' may be a mistake because neither type sufficiently overlaps with the other. If this was intentional, convert the expression to 'unknown' first.
          (panel.byName[panelName] as PanelsLevel)
        : null;

    const parameter = panel ? panel.metadata.parameter : null;

    const hasParameterChanged =
      targetPath[i] === currentPath[i] &&
      parameter &&
      currentParams[parameter] !== targetParams[parameter];

    if (targetPath[i] !== currentPath[i] || hasParameterChanged) {
      isExactMatch = false;

      if (currentPath[i]) {
        isParent = false;
      }

      if (targetPath[i]) {
        isChild = false;
      }
    }

    if (panelName && !panel) {
      return {
        isAnyMatch: false,
        isChild: false,
        isParent: false,
        isExactMatch: false,
        panelExists: false,
      };
    }
  }

  return {
    isAnyMatch: isChild || isExactMatch || isParent,
    isChild,
    isExactMatch,
    isParent,
    panelExists: true,
  };
}

/**
 * Returns the children path with set param, inclusive of the panel config
 */
export function getParamsInDescendantPath(
  panelConfig: ResolvedPanel<any>
): Array<{
  name: string;
  param: undefined | string;
}> {
  const result = [
    {name: panelConfig.name, param: panelConfig.metadata?.parameter},
  ];

  if (panelConfig.children && panelConfig.children.length > 0) {
    return result.concat(
      panelConfig.children.flatMap(getParamsInDescendantPath)
    );
  }

  return result;
}

export function transitionToPanel(
  panelName: PanelName,
  parameters?: Parameters
) {
  return Promise.reject({redirectTo: {panelName, parameters}});
}
